import { Component, computed, inject, output, signal } from '@angular/core';
import { ENV } from '../core-ng/injection-token';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { ElementTypeRegistryService } from '../element-type-registry-service/element-type-registry.service';
import { SurveyBuilderService } from '../survey-builder/survey-builder.service';

@Component({
  selector: 'lib-create-mixed-grid-element-dialog',
  imports: [MatDialogModule, MatButtonModule, CommonModule],
  templateUrl: './create-mixed-grid-element-dialog.html',
  styleUrl: './create-mixed-grid-element-dialog.scss',
})
export class CreateMixedGridElementDialog {
  searchQuery = signal<string>('');

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value.trim());
  }
  private readonly su_url = inject(ENV).supabaseUrl as string;

  private readonly surveyBuilderService = inject(
    SurveyBuilderService,
  );
  selectedType = signal<string | null>(null);

  elementTypesList = signal<any[]>([]);
  private elementTypesMap = computed(() => {
    const map: Record<string, any> = {};
    for (const item of this.elementTypesList()) {
      map[item.id] = {
        ...item,
        icon_path: `${this.su_url}/storage/v1/object/public/assets/${item.icon_path}`,
      };
    }
    return map;
  });
  filteredTypes = computed(() => {
    const query = this.searchQuery().toLowerCase();
    return Object.entries(this.elementTypesMap())
      .filter(
        ([key, config]) =>
          !query ||
          config.title.toLowerCase().includes(query) ||
          config.description.toLowerCase().includes(query),
      )
      .map(([key, config]) => ({ key, config }));
  });

  selectElementType(typeKey: string): void {
    this.selectedType.set(typeKey);
  }
  readonly dialogRef = inject(MatDialogRef<CreateMixedGridElementDialog>);
  readonly data = inject(MAT_DIALOG_DATA);
  onCancel() {
    this.dialogRef.close();
  }

  async create(): Promise<void> {
    const typeKey = this.selectedType();

    if (!typeKey) {
      return;
    }

    const config = this.elementTypesMap()[typeKey];

    // Existing element -> change question type
    if (this.data?.element_id) {
      try {
        const payload = {
          page_id: this.data.page_id,
          element_id: this.data.element_id,
          elementTypeId: config.id,
          survey_id: this.data.survey_id,
        };

        const result = await lastValueFrom(
          this.surveyBuilderService.changeQuestionType(payload),
        );

        this.dialogRef.close({
          action: 'question-type-changed',
          result,
          element_id: this.data.element_id,
          type: config.question_type,
        });
      } catch (error) {
        console.error(
          'Failed to change question type:',
          error,
        );
      }

      return;
    }

    // New element -> keep existing behavior
    this.dialogRef.close(config);
  }
  private readonly elementTypeRegistryService = inject(
    ElementTypeRegistryService,
  );
  async ngOnInit(): Promise<void> {
    try {
      const el = await lastValueFrom(
        this.elementTypeRegistryService.getElementTypes(),
      );

      // if (el) this.elementTypesList.set(el);

      if (el && Array.isArray(el)) {
        // Define the target question types you want to keep
        const targetTypes = [
          'open-ended',
          'drop-down-single-choice',
          'drop-down-multiple-choice',
        ];

        // Filter for specific question types and ensure variant_name is null
        const filteredList = el.filter(
          (item: any) =>
            targetTypes.includes(item.question_type) &&
            item.variant_name === null,
        );

        this.elementTypesList.set(filteredList);
      }
    } catch (err) {
      console.error('Failed to load element registries:', err);
    }
  }
}
